import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'

const module = 'events/eventPoolsModule'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    name: '',
    description: '',
    created_at: '',
    updated_at: '',
    partner: { id: null, name: null },
    agency: { id: null, name: null },
    client: { id: null, name: null }
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

/**
 * Returns a copy of the default form.
 *
 * @return Object
 */
function formDefaults () {
  return {
  }
}

function transformCreate (data) {
  return {
    name: data.name,
    description: data.description,
    partner_id: data.partner.id,
    agency_id: data.agency.id,
    client_id: data.client.id
  }
}

function transformUpdate (data) {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    partner_id: data.partner.id,
    agency_id: data.agency.id,
    client_id: data.client.id
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  isModified: false,
  isLoading: false,
  search_mode: 'client-profile'
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  },

  /**
   * Returns the current selected search mode.
   *
   * @param state
   * @returns {string}
   */
  getSearchMode (state) {
    return state.search_mode
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`events/pools/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    context.commit('reset')
    return ApiService
      .get('events/pools/create')
      .then((response) => {
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`events/pools/${id}`)
      .then((response) => {
        const data = response.data.data
        context.commit('set', data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    return ApiService
      .post(`events/pools`, transformCreate(context.getters['get']))
      .then((response) => {
        context.commit('setIsModified', false)
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    let id = context.getters['get'].id
    return ApiService
      .put(`events/pools/${id}`, transformUpdate(context.getters['get']))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    let id = context.getters['get'].id
    return ApiService
      .delete(`events/pools/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`events/pools`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data = Object.assign(defaults(), data)
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets the search mode used for the assignment widget.
   *
   * @param state
   * @param search_mode
   */
  setSearchMode (state, search_mode) {
    state.search_mode = search_mode
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property
   * and replaced it with a new element.
   *
   * @param state   Object
   * @param name    String Name of property
   * @param index   Number The index of the element to remove
   * @param replacement New element to replace the old
   */
  spliceProp (state, { name, index, replacement }) {
    state.isModified = true
    state.data[name].splice(index, 1, replacement)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = Object.assign({}, defaults())
    state.form = Object.assign({}, formDefaults())
    state.isModified = false
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/events/pools/eventPoolsModule.js